import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { cn } from '../../lib/utils';

/**
 * A part of an arrival exactly as the agent was given it — the whole of it, in a box that scrolls
 * rather than a clamp that cuts, so it can be read, selected and quoted.
 *
 * The run row stores an excerpt (2,000 characters for a destination's part, 400 for a discard). When
 * that excerpt is the whole part it is shown as is; when it was cut, the rest is fetched once, on
 * first render — the server rebuilds it from the recorded cut and checks it against the excerpt and
 * length it stored. Older parts it could only read forward from the excerpt say so; a part it cannot
 * rebuild at all keeps its excerpt with a line saying it is partial.
 */
export function PartText({
  excerpt,
  chars,
  truncated,
  projectId,
  runId,
  part,
  className,
}: {
  excerpt: string;
  chars: number;
  truncated: boolean;
  /** The project the request is made from — any level whose cone holds the run. */
  projectId: string;
  runId: string;
  part: { destination: string; item: number } | { discard: number };
  className?: string;
}) {
  const [full, setFull] = useState<{ text: string; exact: boolean } | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'unavailable' | 'error'>('idle');
  const partKey = 'discard' in part ? `d${part.discard}` : `${part.destination}:${part.item}`;

  useEffect(() => {
    if (!truncated) return;
    let cancelled = false;
    setState('loading');
    api.kb
      .runPartText(projectId, runId, part)
      .then((result) => {
        if (cancelled) return;
        if (result) {
          setFull(result);
          setState('idle');
        } else setState('unavailable');
      })
      .catch(() => !cancelled && setState('error'));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truncated, projectId, runId, partKey]);

  const text = full?.text ?? excerpt;
  return (
    <div className="space-y-1">
      <div className="max-h-[32rem] overflow-y-auto rounded-md border border-border-subtle bg-surface-subtle">
        <pre className={cn('whitespace-pre-wrap break-words p-3 font-sans text-[11px] leading-relaxed text-ink-secondary select-text', className)}>
          {text}
          {truncated && !full && '…'}
        </pre>
      </div>
      {state === 'loading' && <p className="text-[10px] text-ink-faint">Loading the rest of it ({chars.toLocaleString()} characters)…</p>}
      {(state === 'unavailable' || state === 'error') && (
        <p className="text-[10px] text-warning-text">
          Only the first {excerpt.length.toLocaleString()} of {chars.toLocaleString()} characters could be shown — the rest
          {state === 'error' ? ' could not be loaded.' : ' cannot be rebuilt for this run.'}
        </p>
      )}
      {full && !full.exact && (
        <p className="text-[10px] text-ink-faint">
          Rebuilt from an older run's record: the start and length are exact, but if this part joined separate passages
          the seams are not marked.
        </p>
      )}
    </div>
  );
}
